/**
 * PipelineQueue — admin dashboard for the Phase 2.5 content pipeline.
 *
 * Section 7 of the brief: Draft / Sent Back / Locked tabs, filter by era +
 * scope, bulk actions (Lock multiple / Send back multiple with shared notes).
 * Each row shows title, era, scope, source (manual or pipeline), revision
 * count, age in current status.
 */
import { useEffect, useState } from 'react';
import {
  Inbox, Lock, RotateCcw, Loader2, AlertCircle,
  Activity, ArrowRight, Eye, Send, X,
} from 'lucide-react';
import api from '../lib/api';

const STATUS_TABS = [
  { key: 'draft', label: 'Draft', testid: 'tab-draft' },
  { key: 'in_review', label: 'In Review', testid: 'tab-in-review' },
  { key: 'needs_revision', label: 'Sent Back', testid: 'tab-needs-revision' },
  { key: 'locked', label: 'Locked', testid: 'tab-locked' },
  { key: 'all', label: 'All', testid: 'tab-all' },
];

function relativeTime(iso) {
  if (!iso) return '—';
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 60_000) return 'just now';
  const m = Math.floor(ms / 60_000);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

export default function PipelineQueue({ onOpenChain }) {
  const [statusTab, setStatusTab] = useState('draft');
  const [eraFilter, setEraFilter] = useState('');
  const [items, setItems] = useState([]);
  const [diagnostics, setDiagnostics] = useState(null);
  const [busy, setBusy] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [showBulkSendBack, setShowBulkSendBack] = useState(false);
  const [bulkNotes, setBulkNotes] = useState('');
  const [actionMsg, setActionMsg] = useState('');

  const reload = async () => {
    setBusy(true);
    try {
      const params = new URLSearchParams({ status: statusTab });
      if (eraFilter) params.set('era', eraFilter);
      const [q, d] = await Promise.all([
        api.get(`/admin/pipeline/queue?${params}`),
        api.get('/admin/pipeline/diagnostics'),
      ]);
      setItems(q.data || []);
      setDiagnostics(d.data || null);
    } catch (e) {
      setActionMsg(e.response?.data?.detail || 'Could not load queue.');
    }
    setBusy(false);
    setSelected(new Set());
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [statusTab, eraFilter]);

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const selectAll = () => {
    if (selected.size === items.length) setSelected(new Set());
    else setSelected(new Set(items.map((i) => i.id)));
  };

  const bulkLock = async () => {
    if (selected.size === 0) return;
    setActionMsg('');
    try {
      const r = await api.post('/admin/pipeline/bulk-lock', { chain_ids: [...selected] });
      const okCount = r.data?.results?.filter((x) => x.ok).length || 0;
      setActionMsg(`Locked ${okCount} of ${selected.size} chains.`);
      reload();
    } catch (e) {
      setActionMsg(e.response?.data?.detail || 'Bulk lock failed.');
    }
  };

  const bulkSendBack = async () => {
    if (bulkNotes.trim().length < 10) { setActionMsg('Notes must be at least 10 characters.'); return; }
    if (selected.size === 0) return;
    setActionMsg('');
    try {
      const r = await api.post('/admin/pipeline/bulk-send-back', {
        chain_ids: [...selected], review_notes: bulkNotes.trim(),
      });
      const okCount = r.data?.results?.filter((x) => x.ok).length || 0;
      setActionMsg(`Sent back ${okCount} of ${selected.size} chains.`);
      setShowBulkSendBack(false);
      setBulkNotes('');
      reload();
    } catch (e) {
      setActionMsg(e.response?.data?.detail || 'Bulk send back failed.');
    }
  };

  return (
    <div className="p-4 md:p-6" data-testid="pipeline-queue">
      <div className="flex items-center gap-2 mb-2">
        <Inbox size={16} className="text-[#D4A84A]" />
        <span className="font-hud text-[10px] md:text-xs text-[#D4A84A] tracking-widest uppercase">Content Pipeline</span>
      </div>
      <h2 className="font-historical text-[#D4A84A] text-3xl md:text-4xl lg:text-5xl font-light tracking-tight mb-1">
        Review Queue
      </h2>
      <p className="font-mono text-xs md:text-sm text-white/65 mb-4">
        Drafts pending Lock or Send Back. Pipeline ingest writes here.
      </p>

      {/* Diagnostics strip */}
      {diagnostics && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 mb-5" data-testid="pipeline-diagnostics">
          {Object.entries({
            'Draft': diagnostics.counts.draft,
            'In review': diagnostics.counts.in_review,
            'Sent back': diagnostics.counts.needs_revision,
            'Locked': diagnostics.counts.locked,
            'Pipeline total': diagnostics.counts.pipeline_total,
          }).map(([label, n]) => (
            <div key={label} className="border border-[#2F8A92]/15 rounded p-2 text-center">
              <div className="font-historical text-xl md:text-2xl text-[#D4A84A] font-light">{n}</div>
              <div className="font-hud text-[8px] md:text-[9px] tracking-widest text-white/65 uppercase">{label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        {STATUS_TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setStatusTab(t.key)}
            data-testid={`pipeline-${t.testid}`}
            className={`px-3 py-1.5 font-hud text-[10px] md:text-xs tracking-widest uppercase rounded transition-colors ${
              statusTab === t.key ? 'text-[#D4A84A] border border-[#D4A84A]/40 bg-[#D4A84A]/5' : 'text-white/65 border border-transparent hover:text-[#D4A84A]'
            }`}
          >
            {t.label}
          </button>
        ))}
        <select
          value={eraFilter}
          onChange={(e) => setEraFilter(e.target.value)}
          data-testid="pipeline-era-filter"
          className="bg-[#0A0A0A] border border-[#2F8A92]/25 rounded px-2 py-1 font-mono text-xs text-white/85 outline-none ml-auto"
        >
          <option value="">All eras</option>
          {['deep_time', 'ancient', 'classical', 'medieval', 'early_modern',
            'modern', 'industrial', 'twentieth', 'contemporary'].map((e) => (
              <option key={e} value={e}>{e.replace('_', ' ')}</option>
            ))}
        </select>
      </div>

      {/* Bulk actions */}
      {selected.size > 0 && (
        <div className="mb-4 px-3 py-2 border border-[#D4A84A]/30 bg-[#D4A84A]/5 rounded flex flex-wrap items-center gap-2" data-testid="pipeline-bulk-bar">
          <span className="font-mono text-xs text-white/85">{selected.size} selected</span>
          <button
            onClick={bulkLock}
            data-testid="pipeline-bulk-lock"
            className="font-hud text-[10px] tracking-widest text-[#0A0A0A] bg-[#D4A84A] px-3 py-1.5 hover:bg-[#D4A84A]/85 inline-flex items-center gap-1.5 ml-auto"
          >
            <Lock size={11} /> BULK LOCK
          </button>
          <button
            onClick={() => setShowBulkSendBack(true)}
            data-testid="pipeline-bulk-send-back"
            className="font-hud text-[10px] tracking-widest text-white/85 border border-[#FF6B6B]/40 px-3 py-1.5 hover:text-[#FF6B6B] inline-flex items-center gap-1.5"
          >
            <RotateCcw size={11} /> BULK SEND BACK
          </button>
        </div>
      )}

      {showBulkSendBack && (
        <div className="mb-4 px-3 py-3 border border-[#FF6B6B]/30 bg-[#FF6B6B]/5 rounded" data-testid="pipeline-bulk-send-back-form">
          <div className="font-hud text-[10px] tracking-widest text-[#FF6B6B] uppercase mb-2">Bulk send back · shared notes</div>
          <textarea
            value={bulkNotes}
            onChange={(e) => setBulkNotes(e.target.value)}
            placeholder="One set of notes attached to all selected chains. Min 10 chars."
            rows={3}
            data-testid="pipeline-bulk-notes"
            className="w-full bg-[#0A0A0A] border border-[#FF6B6B]/30 rounded px-2 py-1.5 font-mono text-xs text-white/85 outline-none focus:border-[#FF6B6B]/55"
          />
          <div className="flex gap-2 mt-2">
            <button
              onClick={bulkSendBack}
              data-testid="pipeline-bulk-send-back-confirm"
              className="font-hud text-[10px] tracking-widest text-[#0A0A0A] bg-[#FF6B6B] px-3 py-1.5 hover:bg-[#FF6B6B]/85"
            >
              CONFIRM SEND BACK
            </button>
            <button
              onClick={() => { setShowBulkSendBack(false); setBulkNotes(''); }}
              className="font-hud text-[10px] tracking-widest text-white/65 px-3 py-1.5 hover:text-white"
            >
              <X size={11} className="inline" /> CANCEL
            </button>
          </div>
        </div>
      )}

      {actionMsg && (
        <div className="mb-3 px-3 py-2 border border-[#2F8A92]/25 bg-[#2F8A92]/5 rounded font-mono text-xs text-white/85" data-testid="pipeline-msg">
          {actionMsg}
        </div>
      )}

      {/* List */}
      {busy ? (
        <div className="text-center py-8"><Loader2 size={20} className="animate-spin text-[#2F8A92] mx-auto" /></div>
      ) : items.length === 0 ? (
        <div className="text-center py-8 font-mono text-xs md:text-sm text-white/55" data-testid="pipeline-empty">
          {statusTab === 'draft' ? 'No drafts pending. The queue is clear.' :
           statusTab === 'needs_revision' ? 'Nothing sent back. The orchestrator is keeping up.' :
           `No items in ${statusTab}.`}
        </div>
      ) : (
        <div className="border border-[#2F8A92]/15 rounded">
          <div className="px-3 py-2 border-b border-[#2F8A92]/10 flex items-center gap-3 font-hud text-[10px] tracking-widest text-white/65 uppercase">
            <input
              type="checkbox"
              checked={selected.size === items.length && items.length > 0}
              onChange={selectAll}
              data-testid="pipeline-select-all"
              className="accent-[#D4A84A]"
            />
            <span className="flex-1">{items.length} chain{items.length === 1 ? '' : 's'}</span>
            <span className="hidden sm:inline">Era</span>
            <span className="hidden sm:inline">Source</span>
            <span className="hidden md:inline">Updated</span>
          </div>
          <ul className="divide-y divide-[#2F8A92]/8 max-h-[600px] overflow-y-auto">
            {items.map((c) => (
              <li key={c.id} className="px-3 py-3 hover:bg-[#2F8A92]/05 flex items-start gap-3" data-testid={`pipeline-row-${c.id}`}>
                <input
                  type="checkbox"
                  checked={selected.has(c.id)}
                  onChange={() => toggleSelect(c.id)}
                  data-testid={`pipeline-select-${c.id}`}
                  className="mt-1 accent-[#D4A84A]"
                />
                <div className="flex-1 min-w-0">
                  <div className="font-historical text-base md:text-lg text-white truncate">{c.title || c.subject}</div>
                  <div className="font-mono text-[10px] md:text-xs text-white/55 mt-0.5 flex flex-wrap items-center gap-2">
                    <span className="font-hud uppercase tracking-widest text-[9px] text-[#D4A84A]">{c.chain_status}</span>
                    {c.scope_slug && <span>· {c.scope_slug}</span>}
                    {c.revision_count > 0 && <span>· revisions: {c.revision_count}</span>}
                    {c.requires_teacher_review === false && <span className="text-[#FF6B6B]">· bypass-teacher-review</span>}
                  </div>
                  {c.review_notes && (
                    <div className="mt-1 font-mono text-[11px] text-[#FF6B6B] italic line-clamp-2">
                      “{c.review_notes}”
                    </div>
                  )}
                </div>
                <div className="hidden sm:block text-right">
                  <div className="font-mono text-[10px] text-white/65">{c.era || '—'}</div>
                </div>
                <div className="hidden sm:block text-right">
                  <div className="font-mono text-[10px] text-white/65 capitalize">{c.ingest_source || 'manual'}</div>
                </div>
                <div className="hidden md:block text-right">
                  <div className="font-mono text-[10px] text-white/55">{relativeTime(c.updated_at)}</div>
                </div>
                <button
                  onClick={() => onOpenChain?.(c.id)}
                  data-testid={`pipeline-open-${c.id}`}
                  className="text-white/55 hover:text-[#D4A84A]"
                  title="Open chain in context"
                >
                  <Eye size={14} />
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      <p className="font-mono text-[10px] text-white/35 mt-4 leading-relaxed">
        Honest-date principle · Drafts are visible only to admin in their correct spine position. Locking activates spine + dropdown + ticker. Send Back fires the outbound webhook with notes.
      </p>
    </div>
  );
}
